import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

const PUBLISHER_JWT_KEY = 'publisher_jwt';
const API_URL = process.env.REACT_APP_API_URL || 'https://videoreel-api-habwcma9cygwhphm.westus-01.azurewebsites.net';

function getPublisherToken() {
  return localStorage.getItem(PUBLISHER_JWT_KEY) || '';
}

// ─── Styled components (mirrors PublisherAnalyticsDashboard for visual parity) ──

const Wrap = styled.div`color: white;`;

const Title = styled.h1`
  font-size: 1.35rem;
  font-weight: 700;
  margin: 0 0 4px;
  background: linear-gradient(90deg, #fff, rgba(255,255,255,0.7));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SubTitle = styled.p`
  font-size: 0.82rem;
  color: rgba(255,255,255,0.4);
  margin: 0 0 24px;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 28px;
  flex-wrap: wrap;
`;

const PeriodSelect = styled.select`
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 8px;
  color: white;
  font-size: 0.88rem;
  padding: 8px 12px;
  outline: none;
  cursor: pointer;
  &:focus { border-color: rgba(255,71,87,0.5); }
  option { background: #1a1a2e; }
`;

const KpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
  margin-bottom: 28px;
`;

const KpiCard = styled.div`
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 14px;
  padding: 20px 18px;
`;

const KpiLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: rgba(255,255,255,0.35);
  margin-bottom: 8px;
`;

const KpiValue = styled.div`
  font-size: 1.9rem;
  font-weight: 700;
  color: white;
  line-height: 1;
`;

const KpiSub = styled.div`
  font-size: 0.72rem;
  color: rgba(255,255,255,0.35);
  margin-top: 4px;
`;

const Section = styled.div`
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 16px;
  padding: 20px 22px;
  margin-bottom: 20px;
`;

const SectionTitle = styled.div`
  font-size: 0.78rem;
  font-weight: 600;
  color: rgba(255,255,255,0.35);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 18px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
`;

// ─── Clicks-vs-conversions bar chart ───────────────────────────────────────────

const ChartWrap = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
  height: 100px;
  overflow-x: auto;
  padding-bottom: 4px;
`;

const BarCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
`;

const BarStack = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 80px;
`;

const Bar = styled.div`
  width: 14px;
  background: ${p => p.$conv ? 'rgba(46,213,115,0.65)' : 'rgba(255,71,87,0.55)'};
  border-radius: 3px 3px 0 0;
  height: ${p => p.$h}px;
  min-height: 2px;
  transition: height 0.2s;
`;

const BarLabel = styled.div`
  font-size: 0.6rem;
  color: rgba(255,255,255,0.25);
  white-space: nowrap;
  transform: rotate(-45deg);
  transform-origin: top left;
  margin-top: 4px;
`;

const Legend = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 14px;
  font-size: 0.72rem;
  color: rgba(255,255,255,0.4);
`;

const LegendDot = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  margin-right: 6px;
  background: ${p => p.$color};
  vertical-align: middle;
`;

// ─── Horizontal country bars ───────────────────────────────────────────────────

const CountryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  font-size: 0.82rem;
`;

const CountryLabel = styled.div`
  width: 130px;
  flex-shrink: 0;
  color: rgba(255,255,255,0.75);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CountryBarTrack = styled.div`
  flex: 1;
  height: 14px;
  background: rgba(255,255,255,0.05);
  border-radius: 7px;
  overflow: hidden;
`;

const CountryBarFill = styled.div`
  height: 100%;
  width: ${p => p.$pct}%;
  background: rgba(255,71,87,0.55);
  border-radius: 7px;
`;

const CountryCount = styled.div`
  width: 64px;
  flex-shrink: 0;
  text-align: right;
  color: rgba(255,255,255,0.5);
  font-size: 0.78rem;
`;

// ─── Device split ──────────────────────────────────────────────────────────────

const DeviceRow = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const DeviceChip = styled.div`
  flex: 1;
  min-width: 120px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 12px;
  padding: 14px 16px;
  text-align: center;
`;

const DeviceVal = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  color: white;
`;

const DeviceName = styled.div`
  font-size: 0.72rem;
  color: rgba(255,255,255,0.4);
  text-transform: capitalize;
  margin-top: 4px;
`;

// ─── Recent clicks table ───────────────────────────────────────────────────────

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px 10px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255,255,255,0.35);
  border-bottom: 1px solid rgba(255,255,255,0.06);
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 10px 10px;
  color: rgba(255,255,255,0.75);
  border-bottom: 1px solid rgba(255,255,255,0.04);
  white-space: nowrap;
  &:first-child { color: white; }
`;

const PubPill = styled.span`
  font-size: 0.7rem;
  padding: 2px 8px;
  background: rgba(255,71,87,0.1);
  border: 1px solid rgba(255,71,87,0.2);
  color: #ff7c89;
  border-radius: 20px;
  white-space: nowrap;
`;

const EmptyMsg = styled.div`
  text-align: center;
  padding: 48px 0;
  color: rgba(255,255,255,0.3);
  font-size: 0.88rem;
`;

const ErrorMsg = styled.div`
  background: rgba(255,71,87,0.08);
  border: 1px solid rgba(255,71,87,0.2);
  border-radius: 10px;
  padding: 14px 16px;
  color: #ff7c89;
  font-size: 0.85rem;
  margin-bottom: 20px;
`;

// ─── Helpers ───────────────────────────────────────────────────────────────────

const RANGE_OPTIONS = [
  { val: '1d', label: 'Today' },
  { val: '7d', label: 'Last 7 days' },
  { val: '30d', label: 'Last 30 days' },
  { val: '90d', label: 'Last 90 days' },
  { val: 'all', label: 'All time' },
];

const DEVICE_ICONS = { mobile: '📱', tablet: '📲', desktop: '🖥️', bot: '🤖' };

// ISO-3166 alpha-2 → flag emoji (regional indicator symbols)
function flagEmoji(cc) {
  if (!cc || cc.length !== 2) return '🌐';
  const base = 0x1f1e6;
  const codePoints = cc.toUpperCase().split('').map(c => base + (c.charCodeAt(0) - 65));
  try {
    return String.fromCodePoint(...codePoints);
  } catch {
    return '🌐';
  }
}

function formatClickedAt(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (sameDay) return `${time} today`;
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}`;
}

// ─── Component ─────────────────────────────────────────────────────────────────

export default function PublisherInviteClicks() {
  const [range, setRange] = useState('7d');
  const [publicationId, setPublicationId] = useState('');
  const [pubs, setPubs] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Load the publisher's publications for the filter dropdown
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/publisher/publications`, {
          headers: { Authorization: `Bearer ${getPublisherToken()}` },
        });
        if (res.ok) {
          const json = await res.json();
          setPubs(json.publications || []);
        }
      } catch {
        // non-critical
      }
    })();
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ range });
      if (publicationId) params.set('publication_id', publicationId);
      const res = await fetch(`${API_URL}/api/publisher/invite-clicks?${params.toString()}`, {
        headers: { Authorization: `Bearer ${getPublisherToken()}` },
      });
      if (!res.ok) throw new Error(`Server error (${res.status})`);
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [range, publicationId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const byDay = data?.by_day || [];
  const maxDay = Math.max(1, ...byDay.map(d => d.clicks));
  const byCountry = data?.by_country || [];
  const maxCountry = Math.max(1, ...byCountry.map(c => c.clicks));
  const byDevice = data?.by_device || {};
  const recent = data?.recent || [];
  const totals = data?.totals;

  return (
    <Wrap>
      <Title>Invite Clicks</Title>
      <SubTitle>Top-of-funnel: every click on your invite links and how many converted to a subscribe. Masked data only — no personal information is shown.</SubTitle>

      <Controls>
        <PeriodSelect value={publicationId} onChange={e => setPublicationId(e.target.value)}>
          <option value="">All publications</option>
          {pubs.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </PeriodSelect>
        <PeriodSelect value={range} onChange={e => setRange(e.target.value)}>
          {RANGE_OPTIONS.map(o => (
            <option key={o.val} value={o.val}>{o.label}</option>
          ))}
        </PeriodSelect>
        {loading && <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.35)' }}>Loading…</span>}
      </Controls>

      {error && <ErrorMsg>{error}</ErrorMsg>}

      {totals && (
        <>
          <KpiRow>
            <KpiCard>
              <KpiLabel>Clicks</KpiLabel>
              <KpiValue>{totals.clicks.toLocaleString()}</KpiValue>
              <KpiSub>non-bot beacons</KpiSub>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Unique visitors</KpiLabel>
              <KpiValue>{totals.unique_visitors.toLocaleString()}</KpiValue>
              <KpiSub>distinct masked IPs</KpiSub>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Conversions</KpiLabel>
              <KpiValue>{totals.conversions.toLocaleString()}</KpiValue>
              <KpiSub>clicked & subscribed</KpiSub>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Drop-off</KpiLabel>
              <KpiValue>{totals.drop_off.toLocaleString()}</KpiValue>
              <KpiSub>clicked, no subscribe</KpiSub>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Conversion rate</KpiLabel>
              <KpiValue>{Math.round((totals.conversion_rate || 0) * 100)}%</KpiValue>
              <KpiSub>conversions / clicks</KpiSub>
            </KpiCard>
          </KpiRow>

          {byDay.length > 0 && (
            <Section>
              <SectionTitle>Clicks vs conversions by day</SectionTitle>
              <ChartWrap>
                {byDay.map(d => (
                  <BarCol key={d.date}>
                    <BarStack>
                      <Bar $h={Math.max(2, Math.round((d.clicks / maxDay) * 78))}
                        title={`${d.date}: ${d.clicks} clicks`} />
                      <Bar $conv $h={Math.max(2, Math.round((d.conversions / maxDay) * 78))}
                        title={`${d.date}: ${d.conversions} conversions`} />
                    </BarStack>
                    <BarLabel>{d.date.slice(5)}</BarLabel>
                  </BarCol>
                ))}
              </ChartWrap>
              <Legend>
                <span><LegendDot $color="rgba(255,71,87,0.55)" />Clicks</span>
                <span><LegendDot $color="rgba(46,213,115,0.65)" />Conversions</span>
              </Legend>
            </Section>
          )}

          <Section>
            <SectionTitle>Top countries</SectionTitle>
            {byCountry.length > 0 ? (
              byCountry.slice(0, 10).map(c => (
                <CountryRow key={c.country_code || c.country}>
                  <CountryLabel>{flagEmoji(c.country_code)} {c.country || c.country_code}</CountryLabel>
                  <CountryBarTrack>
                    <CountryBarFill $pct={Math.round((c.clicks / maxCountry) * 100)} />
                  </CountryBarTrack>
                  <CountryCount>{c.clicks.toLocaleString()}</CountryCount>
                </CountryRow>
              ))
            ) : (
              <EmptyMsg>Country not available yet — location data will appear once Cloudflare is enabled.</EmptyMsg>
            )}
          </Section>

          <Section>
            <SectionTitle>Device split</SectionTitle>
            <DeviceRow>
              {['mobile', 'desktop', 'tablet'].map(dev => (
                <DeviceChip key={dev}>
                  <DeviceVal>{DEVICE_ICONS[dev]} {(byDevice[dev] || 0).toLocaleString()}</DeviceVal>
                  <DeviceName>{dev}</DeviceName>
                </DeviceChip>
              ))}
            </DeviceRow>
          </Section>

          <Section>
            <SectionTitle>Recent clicks</SectionTitle>
            {recent.length === 0 ? (
              <EmptyMsg>No invite clicks recorded for this range yet.</EmptyMsg>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Time</Th>
                    <Th>Publication</Th>
                    <Th>Country</Th>
                    <Th>City</Th>
                    <Th>Device / OS</Th>
                    <Th>Converted</Th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((r, i) => (
                    <tr key={`${r.clicked_at}-${i}`}>
                      <Td title={r.ip_masked ? `IP ${r.ip_masked}` : undefined}>{formatClickedAt(r.clicked_at)}</Td>
                      <Td><PubPill>{r.publication_name || r.publication_id}</PubPill></Td>
                      <Td>{r.country_code ? `${flagEmoji(r.country_code)} ${r.country || r.country_code}` : '—'}</Td>
                      <Td>{r.city || '—'}</Td>
                      <Td>{DEVICE_ICONS[r.device_type] || ''} {r.os || r.device_type || '—'}</Td>
                      <Td>{r.converted ? '✅' : '—'}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Section>
        </>
      )}

      {!loading && !error && !totals && (
        <EmptyMsg>Loading invite click data…</EmptyMsg>
      )}
    </Wrap>
  );
}
